/**
 * Starts OpenTelemetry before the app's module graph loads, so API clients created at module
 * scope capture the instrumented `globalThis.fetch` and emit spans.
 *
 * Prefer `loadAfterTelemetry` at client entrypoints so the import barrier is structural.
 * No-ops on the server and never rejects — telemetry must not gate rendering.
 */
export const startTelemetry = async (appName: string, appVersion: string) => {
  if (typeof window === "undefined") return;

  try {
    const { initOpenTelemetry } = await import("./openTelemetry");
    initOpenTelemetry(appName, appVersion);
  } catch (error) {
    console.error("Failed to initialize OpenTelemetry:", error);
  }
};

/**
 * Runs `load` only after telemetry has started (or failed soft).
 * Client entrypoints only — use for `() => import("./App")` (and any mock setup that must wrap
 * the instrumented fetch) so clients cannot capture `fetch` before instrumentation.
 */
export const loadAfterTelemetry = async <T>(
  appName: string,
  appVersion: string,
  load: () => Promise<T>
): Promise<T> => {
  await startTelemetry(appName, appVersion);
  return load();
};
